// Navigation is built from what the session may do at the current outlet (08-clients.md);
// hiding is convenience only, the server checks every action.
import type { Me } from '@qrkey/api-client';
import { PERMISSION_CATALOGUE, type PermissionKey, type TerminalRole } from '@qrkey/contracts';

export function outletPermissions(me: Me | undefined, outletId: string | null): Set<string> {
  if (!me) return new Set();
  const at = me.outlets.find((o) => o.outletId === outletId) ?? me.outlets[0];
  return new Set([...(at?.permissions ?? []), ...me.permissions]);
}

export function hasBackOffice(perms: Set<string>): boolean {
  return [...perms].some((k) => PERMISSION_CATALOGUE[k as PermissionKey]?.scope === 'backoffice');
}

export interface NavItem {
  to: '/pos' | '/captain' | '/kds' | '/till' | '/office';
  label: string;
}

export function navItems(perms: Set<string>, options: { till?: boolean } = {}): NavItem[] {
  const items: NavItem[] = [];
  if (perms.has('orders.create')) items.push({ to: '/pos', label: 'POS' });
  // Shifts and the drawer belong to a till; the back office has no drawer.
  if (
    options.till &&
    ['shift.open', 'shift.close', 'cash.in_out', 'expenses.record'].some((k) => perms.has(k))
  ) {
    items.push({ to: '/till', label: 'Shift and cash' });
  }
  if (perms.has('orders.send_kot')) items.push({ to: '/captain', label: 'Captain' });
  if (perms.has('kitchen.kds')) items.push({ to: '/kds', label: 'Kitchen display' });
  if (hasBackOffice(perms)) items.push({ to: '/office', label: 'Back office' });
  return items;
}

/** The home screen for this device: its terminal role, else back office. */
export function homeFor(role: TerminalRole | undefined, perms: Set<string>): NavItem['to'] {
  if (role === 'kds') return '/kds';
  if (role === 'captain') return '/captain';
  if (role === 'pos' || role === 'hub' || role === 'kiosk') return '/pos';
  const items = navItems(perms);
  return items.find((i) => i.to === '/office')?.to ?? items[0]?.to ?? '/office';
}

/** Short, readable form of an id, shown until the outlet's name has loaded. */
export function shortId(id: string): string {
  return `…${id.slice(-4)}`;
}
